import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { FeedbackService } from '../../service/feedback.service';
import { FeedbackDialogComponent } from '../feedback-dialog/feedback-dialog.component';
import { Router } from '@angular/router';
import { RoleplayApiService } from '../../service/roleplay.service';
import { EvaluateFeedbackComponent } from './evaluate-feedback/evaluate-feedback.component';
import { FormControl } from '@angular/forms';
import { PlatformRegionService } from '../../service/shared/platform-region.service';
import { GridConfig } from '../../v2/models/shared/grid/grid.model';
import { Subject, merge, of } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, startWith, takeUntil, catchError, tap } from 'rxjs/operators';
import { PageEvent } from '@angular/material/paginator';
import { AssignTicketDialogComponent } from '../ContactCenter/assign-ticket-dialog/assign-ticket-dialog.component';
import { ContactCenterApiService } from '../../v2/apis/contact-center/contact-center-api.service';
import { UserService } from '../../service/shared/user-service.service';


@Component({
    selector: 'app-feedback',
    templateUrl: './feedback.component.html',
    styleUrls: ['./feedback.component.scss'],
    standalone: false
})
export class FeedbackComponent implements OnInit, OnDestroy {

  private destroy$ = new Subject<void>();
  private tabChange$ = new Subject<number>();
  private pageChange$ = new Subject<void>();

  feedbackList: any[] = [];
  filteredFeedbackList: any[] = [];
  role: string;
  canViewAllFeedbackTab = false;
  canViewMyFeedbackTab = false;
  canViewAssignedFeedbackTab = false;
  canViewOpenFeedbackTab = false;
  selectedTabIndex: number = 0;
  searchFilter = new FormControl('');
  feedbackTypeFilter = new FormControl('');
  currentPage: number = 0;
  pageSize: number = 5;
  totalCount: number = 0;
  pageSizeOptions: number[] = [5, 10, 25, 50, 100];
  gridConfig: GridConfig = {
    rowClickable: true,
    hidePaginator: true
  };
  feedbackColumns = [
    { label: 'ID', key: 'conversation_feedback_id', type: 'default' },
    { label: 'User Request', key: 'user_request', type: 'default', cell_class: 'user-request-cell', header_class: 'user-request-header' },
    { label: 'Username', key: 'username', type: 'default' },
    { label: 'Feedback Status', key: 'feedback_status', type: 'default' },
    { label: 'Conversation Date', key: 'created_date', type: 'date', date_format: 'd MMM yyyy h:mma' },
    { label: 'Feedback Type', key: 'feedback_type', type: 'default' },
    { label: 'Assignee', key: 'assigned_category', type: 'default' },
    { label: 'Feedback Value', key: 'feedback_value', type: 'default' },
    { label: 'Feedback Comment', key: 'comment', type: 'default' }
  ];

  constructor(
    private feedbackService: FeedbackService,
    private dialog: MatDialog,
    public router: Router,
    private platformRegionService: PlatformRegionService,
    private contactCenterService: ContactCenterApiService,
    private userService: UserService
  ) { }

  ngOnInit(): void {
    const savedTab = sessionStorage.getItem('feedbackSelectedTab');
    this.selectedTabIndex = savedTab ? Number(savedTab) : 0;
    this.initializeTabVisibility();
    this.setupDynamicSearch();
  }

  private initializeTabVisibility(): void {
    this.canViewAllFeedbackTab = this.userService.isSuperVisor() || this.userService.isTrainerUser();
    this.canViewMyFeedbackTab = true;
    this.canViewAssignedFeedbackTab = this.userService.isSuperVisor() || this.userService.isTrainerUser();;
    this.canViewOpenFeedbackTab = this.userService.isTraineeUser();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setupDynamicSearch(): void {
    merge(
      this.searchFilter.valueChanges.pipe(debounceTime(400), distinctUntilChanged(), tap(() => this.currentPage = 0)),
      this.feedbackTypeFilter.valueChanges.pipe(tap(() => this.currentPage = 0)),
      this.tabChange$.pipe(tap(() => this.currentPage = 0)),
      this.pageChange$
    ).pipe(
      startWith(null),
      switchMap(() => {
        this.showLoader();
        const search = (this.searchFilter.value || '').trim();
        const type = (this.feedbackTypeFilter.value || '').trim();
        const offset = this.currentPage * this.pageSize;
        return this.fetchDataByTab(this.selectedTabIndex, search, offset, this.pageSize, type).pipe(
          catchError(() => {
            this.hideLoader();
            return of([]);
          })
        );
      }),
      takeUntil(this.destroy$)
    ).subscribe(data => {
      if (data === null) return;
      this.hideLoader();
      this.totalCount = data?.total_count ?? 0;
      let feedbackArray = this.extractFeedbackArray(data);
      const processedData = this.processData(feedbackArray);
      this.feedbackList = processedData;
      this.filteredFeedbackList = [...processedData];
    });
  }

  // -----------------------------------------------------------------
  // METHOD PURPOSE:
  //   Resolves which feedback API to call for the active tab. Tab index
  //   is role-dependent (tabs are *ngIf-gated), so the visibility flags
  //   decide the logical tab. Only All Feedback omits WWID; My Feedback
  //   (every role) and Open Feedback always send WWID.
  //
  // MODIFICATION HISTORY (latest first – keep ONLY the last 3):
  //   [JHUD-5543] 2026-10-08 | Venkatesan | Fixed role-based tab mapping so My Feedback always sends WWID.
  //   [JHUD-5543] 2026-10-08 | Venkatesan | Treat ENDUSER like TRAINEE for My Feedback (Open status + WWID).
  //   [JHUD-5543] 2026-10-07 | Venkatesan | Omit WWID for All Feedback; send it for My Feedback.
  // -----------------------------------------------------------------
  private fetchDataByTab(tabIndex: number, search: string, offset: number = 0, limit: number = 5, feedbackType?: string) {
    const businessUnitCode = sessionStorage.getItem('businessUnitCode') || '';
    const regionName = this.platformRegionService.selectedRegion?.name || '';
    const wwid = Number(sessionStorage.getItem('userId'));
    const baseRequest = { businessUnitCode, regionName, search, offset, limit, feedbackType };

    // All Feedback (supervisor/trainer, index 0) lists every user's feedback, so WWID is intentionally omitted.
    if (tabIndex === 0 && this.canViewAllFeedbackTab) {
      return this.feedbackService.fetchAllRecords(baseRequest);
    }

    // Assigned Feedback (supervisor/trainer, index 2) - unchanged.
    if (tabIndex === 2 && this.canViewAssignedFeedbackTab) {
      return this.contactCenterService.getFeedbacksByCategory(wwid, businessUnitCode);
    }

    // Open Feedback (trainee, index 1) - status-filtered, with WWID.
    if (tabIndex === 1 && this.canViewOpenFeedbackTab) {
      return this.feedbackService.fetchFeedbackByStatus('Open', { ...baseRequest, wwid });
    }

    // My Feedback (every role) - always send WWID.
    return this.feedbackService.fetchAllRecords({ ...baseRequest, wwid });
  }

  private extractFeedbackArray(data: any): any[] {
    if (Array.isArray(data)) {
      return data.map(item => item.feedback || item);
    } else if (data?.feedback_with_logs && Array.isArray(data.feedback_with_logs)) {
      return data.feedback_with_logs.map(item => item.feedback || item);
    } else if (data?.feedbacks && Array.isArray(data.feedbacks)) {
      return data.feedbacks.map(item => item.feedback || item);
    } else if (data?.data && Array.isArray(data.data)) {
      return data.data.map(item => item.feedback || item);
    }
    return [];
  }


  onTabChange(event: any): void {
    this.selectedTabIndex = event.index;
    sessionStorage.setItem('feedbackSelectedTab', String(event.index));
    this.searchFilter.setValue('', { emitEvent: false });
    this.feedbackTypeFilter.setValue('', { emitEvent: false });
    this.currentPage = 0;
    this.tabChange$.next(event.index);
  }

  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex;
    this.pageSize = event.pageSize;
    this.pageChange$.next();
  }

  showLoader() {
    const loader = document.getElementById('loader');
    loader.style.display = 'inline-block';
  }

  hideLoader() {
    const loader = document.getElementById('loader');
    loader.style.display = 'none';
  }

  openFeedbackDialog(): void {
    const dialogRef = this.dialog.open(FeedbackDialogComponent);

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.tabChange$.next(this.selectedTabIndex);
      }
    });
  }
  convertUtcToLocal(utcDateString: string): string | null {
    if (!utcDateString) return null;
    const utcDate = utcDateString.split('.')[0] + 'Z';
    const localDate = new Date(utcDate);
    const day = String(localDate.getDate());
    const year = localDate.getFullYear();
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const monthShort = monthNames[localDate.getMonth()] || '';

    let hours = localDate.getHours();
    const minutes = String(localDate.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'

    return `${day} ${monthShort} ${year} ${hours}:${minutes}${ampm}`;
  }

  processData(data: any[]): any[] {
    return data.map(item => {
      const utcDateString = item.created_date;
      let dateObj = null;
      let formattedDate = null;

      if (utcDateString) {
        const utcDate = utcDateString.split('.')[0] + 'Z';
        dateObj = new Date(utcDate);
        formattedDate = this.convertUtcToLocal(utcDateString);
      }

      const username = `${item.given_by_first_name || ''} ${item.given_by_last_name || ''}`.trim() || (item.given_by ? `User ${item.given_by}` : '');

      return {
        ...item,
        username: username,
        assigned_category: item.assigned_category || 'Unassigned',
        created_date: dateObj,
        created_date_sort: dateObj
      };
    });
  }
  Tabs(feedbackId: number) {
    this.dialog.open(EvaluateFeedbackComponent, {
      width: '800px',
      data: { message: '', feedbackId: feedbackId }
    });
  }
  onRowClick(element: any): void {
    this.router.navigate(['/app-comment-history'], { queryParams: { feedbackDetails: element }, skipLocationChange: true });
  }

  onAssigneeCellClick(element: any): void {
    const dialogRef = this.dialog.open(AssignTicketDialogComponent, {
      width: '450px',
      data: {
        ticketId: element.conversation_feedback_id,
        currentAssignee: element.assignee
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result?.assignee) {
        element.assignee = result.assignee;
      }
    });
  }
}